import { revalidateData } from '../actions/revalidate';

const RAW_API_BASE =
  process.env.NEXT_PUBLIC_API_URL ||
  "https://smart-backend1.apps.teh2.abrhapaas.com/";

const withTrailingSlash = (value) =>
  value.endsWith("/") ? value : `${value}/`;

const resolveApiBaseUrl = () => {
  const prefix = "/api/";

  if (typeof window === "undefined") {
    const origin = (
      process.env.API_INTERNAL_URL || "http://backend:5050"
    ).replace(/\/+$/, "");
    return `${origin}${prefix}`;
  }

  const base = (RAW_API_BASE || prefix).trim() || prefix;
  if (/^https?:\/\//i.test(base)) {
    return withTrailingSlash(base);
  }

  const path = base.startsWith("/") ? base : `/${base}`;
  return `${window.location.origin}${withTrailingSlash(path)}`;
};

export const getApiBaseUrl = resolveApiBaseUrl;
export const API_BASE_URL = resolveApiBaseUrl();
const DEFAULT_REVALIDATE = 3600; // 1 hour ISR

// Cache duration configurations for different resource types
const CACHE_CONFIG = {
  products: { revalidate: 3600, tags: ['products'] },
  categories: { revalidate: 7200, tags: ['categories'] },
  brands: { revalidate: 7200, tags: ['brands'] },
  materials: { revalidate: 7200, tags: ['materials'] },
  blogs: { revalidate: 3600, tags: ['blogs'] },
  stores: { revalidate: 7200, tags: ['stores'] },
  'selected-products': { revalidate: 1800, tags: ['selected-products', 'products'] },
  'selected-stores': { revalidate: 1800, tags: ['selected-stores', 'stores'] },
  faq: { revalidate: 7200, tags: ['faq'] },
  meta: { revalidate: 7200, tags: ['meta'] },
  statics: { revalidate: 86400, tags: ['statics'] },
  users: { revalidate: 0, tags: ['users'] }, // Admin only, no cache
};

// Get auth token (server/client safe)
const getAuthToken = () => 
  typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

// Encode path segments once (handles Persian slugs and pre-encoded params)
const encodeEndpointPath = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  return cleanEndpoint
    .split("/")
    .map((segment) => {
      if (!segment) return segment;
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join("/");
};

// Build URL with query params
const buildUrl = (endpoint, params = {}) => {
  const resolvedBase = resolveApiBaseUrl();
  const baseUrl = resolvedBase.endsWith('/') ? resolvedBase : `${resolvedBase}/`;
  const url = new URL(`${baseUrl}${encodeEndpointPath(endpoint)}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      url.searchParams.append(key, String(value));
    }
  });
  return url.toString();
};

// Extract resource type from endpoint for cache tags
const getResourceType = (endpoint) => {
  const segments = endpoint.split('/').filter(Boolean);
  return segments[0] || 'default';
};

/**
 * Enhanced API request with ISR and tag-based revalidation
 */
export const apiRequest = async (endpoint, options = {}) => {
  const { params, next, ...fetchOptions } = options;
  const isGet = !fetchOptions.method || fetchOptions.method === 'GET';
  const token = getAuthToken();
  const resourceType = getResourceType(endpoint);
  const cacheConfig = CACHE_CONFIG[resourceType] || { revalidate: DEFAULT_REVALIDATE, tags: [resourceType] };

  // Build headers
  const headers = {
    ...(!(fetchOptions.body instanceof FormData) && { 'Content-Type': 'application/json' }),
    ...(token && !endpoint.includes('auth/login') && { 'Authorization': `Bearer ${token}` }),
    ...fetchOptions.headers,
  };

  const url = buildUrl(endpoint, params);
  
  // Determine cache strategy
  const isAdminRequest = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/admin');
  const noCache = options.cache === 'no-store' || options.revalidate === 0 || isAdminRequest;

  // Build fetch options with optimized caching
  const finalFetchOptions = {
    ...fetchOptions,
    headers,
    ...(isGet ? {
      cache: noCache ? 'no-store' : 'force-cache',
      next: {
        revalidate: noCache ? 0 : (next?.revalidate ?? cacheConfig.revalidate),
        tags: [...new Set([...cacheConfig.tags, ...(next?.tags || []), `${resourceType}-item`])],
      }
    } : {
      cache: 'no-store'
    })
  };

  try {
    const response = await fetch(url, finalFetchOptions);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: await response.text() || response.statusText };
      }

      // Handle 401 unauthorized
      if (response.status === 401 && typeof window !== 'undefined') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
      }

      const error = new Error(errorData.message || 'API Request failed');
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    if (response.status === 204) return { success: true };

    const data = await response.json();

    // Auto-revalidate on mutations (POST, PATCH, PUT, DELETE)
    // Only revalidate if the mutation was successful
    if (!isGet && !endpoint.includes('auth/login')) {
      try {
        const tagsToRevalidate = [...cacheConfig.tags];
        
        // Add specific item tag if we have an ID
        const idMatch = endpoint.match(/\/(\d+|[a-f0-9-]{36})$/i);
        if (idMatch) {
          tagsToRevalidate.push(`${resourceType}-${idMatch[1]}`);
        }
        
        // Execute revalidation asynchronously without blocking the response
        revalidateData(tagsToRevalidate);
      } catch (err) {
        // Revalidation failure shouldn't break the request
      }
    }

    return normalizeApiResponse(data);
  } catch (error) {
    console.error('[API Error]', error);
    throw error;
  }
};

/**
 * Normalize API response to consistent format
 */
const normalizeApiResponse = (data) => {
  if (!data || typeof data !== "object") return data;

  const result = data.data || data;

  // Check for pagination indicators
  const hasPagination = result && typeof result === "object" && 
    ("totalPages" in result || "total" in result || "last_page" in result || 
     "lastPage" in result || "total_count" in result || "count" in result);

  // Find data array
  const dataArray = result && typeof result === "object"
    ? result.data || (Array.isArray(result) ? result : null) ||
      result.items || result.results || result.users || result.products ||
      result.posts || result.blogs || result.brands || result.categories ||
      result.stores || null
    : null;

  // Return paginated response
  if (hasPagination) {
    return {
      data: dataArray || [],
      total: result.total ?? result.total_count ?? result.count ?? 
             (Array.isArray(dataArray) ? dataArray.length : 0),
      totalPages: result.totalPages ?? result.last_page ?? result.lastPage ?? 1,
      currentPage: result.currentPage ?? result.current_page ?? 1,
    };
  }

  // Return single resource if it has an ID
  if (result && typeof result === "object" && !Array.isArray(result)) {
    if (result.id || result._id || result.uuid || result.UUID) {
      return result;
    }
    // Unwrap nested single resource (e.g. { category: { id: ... } })
    const keys = Object.keys(result);
    if (keys.length === 1) {
      const inner = result[keys[0]];
      if (inner && typeof inner === "object" && !Array.isArray(inner) && (inner.id || inner._id || inner.uuid || inner.UUID)) {
        return inner;
      }
    }
  }

  // Return array if found
  if (dataArray && Array.isArray(dataArray)) {
    return dataArray;
  }

  return result;
};

export const toUrlEncoded = (data) => {
  const formData = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  return formData.toString();
};

export const toFormData = (data) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    
    const fieldKey = (Array.isArray(value) && !key.endsWith('[]') && key !== 'images') 
      ? `${key}[]` 
      : key;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item instanceof File) {
          formData.append(fieldKey, item);
        } else if (item !== null && item !== undefined) {
          formData.append(fieldKey, item);
        }
      });
    } else if (value instanceof File || value instanceof FileList) {
      if (value instanceof FileList) {
        Array.from(value).forEach((file) => formData.append(fieldKey, file));
      } else {
        formData.append(fieldKey, value);
      }
    } else {
      formData.append(fieldKey, value);
    }
  });
  return formData;
};
